import React, { createContext, useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { initialGroup } from './models';
import { createEmptyFacility } from './validationSchemas';
import { createNewListing } from './models';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { transformFacilityForApi } from './transformers/facility-transformer';
import { 
  GroupIntakeProvider as ModernGroupIntakeProvider,
  useGroupCore,
  useFacility,
  useListing,
  useError
} from './contexts/GroupIntakeRootContext';

/**
 * Legacy context for maintaining backward compatibility
 * while transitioning to the new modular context architecture
 * @see ./contexts/ for the new implementation
 */
export const GroupIntakeContext = createContext();

/**
 * Legacy Provider implementation that maintains the original API
 * This is a simplified version of the original context to maintain backward compatibility
 * while we transition all components to use the new modular contexts
 */
export const LegacyGroupIntakeProvider = ({ children }) => {
  // Connect to authentication
  const { auth } = useContext(AuthContext);
  
  // Form state
  const [currentStep, _setCurrentStep] = useState(0);
  const [group, setGroup] = useState({ ...initialGroup });
  const [facilities, setFacilities] = useState([createEmptyFacility()]); 
  const [errors, setErrors] = useState({});
  // State for tracking if we're editing an existing group
  const [existingGroupId] = useState(null);
  
  // UI state
  const [isLoading, setIsLoading] = useState(false);
  // Form submission state
  const [isSubmitting] = useState(false);
  const [submitSuccess] = useState(false);
  
  // Preview state
  const [previewFacilityIndex, setPreviewFacilityIndex] = useState(0);
  const [previewListingIndex, setPreviewListingIndex] = useState(0);
  
  // Modal state
  const [showDelFacModal, setShowDelFacModal] = useState(false);
  const [facToRemove, setFacToRemove] = useState(null);
  const [showDelListModal, setShowDelListModal] = useState(false);
  const [listToRemove, setListToRemove] = useState({ facIdx: null, listIdx: null });

  // Autosave functionality with debounce
  const [autosaveStatus] = useState({ message: '', type: '' });
  
  // Explicitly define setCurrentStep function for legacy components
  const setCurrentStep = useCallback((step) => {
    _setCurrentStep(step);
  }, []);
  
  // Simplified functions that will be replaced by the new contexts in actual components
  const updateGroup = useCallback((field, value) => {
    setGroup(prev => {
      const updated = { ...prev };
      if (field.includes('.')) {
        const parts = field.split('.');
        let current = updated;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
      } else {
        updated[field] = value;
      }
      return updated;
    });
  }, []);
  
  const addFacility = useCallback(async () => {
    // Create a new facility instance
    const newFacility = createEmptyFacility();
    
    // Update local state immediately for responsive UI
    setFacilities(prev => [...prev, newFacility]);
    
    // If we have an existing group and auth token, create on backend
    if (existingGroupId && auth?.token) {
      try {
        // Transform the facility to match backend schema
        const transformedFacility = transformFacilityForApi(newFacility);
        console.log('Creating facility with backend schema:', transformedFacility);
        
        const response = await fetch(`${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/groups/${existingGroupId}/facilities`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.token}`
          },
          body: JSON.stringify(transformedFacility)
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          console.error('Failed to create facility:', errorData);
          throw new Error(errorData.message || 'Failed to create facility');
        }
        
        const data = await response.json();
        console.log('Facility created successfully:', data);
        
        // Update the facility in state with the returned ID
        setFacilities(prev => {
          const updated = [...prev];
          const index = updated.length - 1;
          updated[index] = {
            ...updated[index],
            id: data.id || data._id,
            _id: data.id || data._id
          };
          return updated;
        });
      } catch (error) {
        console.error('Error creating facility:', error);
        setErrors?.(prev => ({
          ...prev,
          facility: error.message || 'Failed to create facility'
        }));
      }
    } else {
      console.log('Skipping API call - no group ID or auth token');
    }
  }, [existingGroupId, auth, setErrors]);
  
  // API client for facility updates
  const callFacilityApi = useCallback(async (facilityId, fieldData) => {
    if (!facilityId || !auth?.token) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${process.env.REACT_APP_RXROOSTER_BACKEND_URL}/facilities/${facilityId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.token}`
        },
        body: JSON.stringify(fieldData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Facility update failed:', errorData);
        setErrors(prev => ({
          ...prev,
          facility: errorData.message || 'Failed to update facility'
        }));
        return null;
      }
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating facility:', error);
      setErrors(prev => ({
        ...prev,
        facility: error.message || 'Network error when updating facility'
      }));
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [auth, setErrors]);
  
  const updateFacility = useCallback((facilityIndex, field, value) => {
    // Update local state for immediate UI feedback
    setFacilities(prev => {
      const updated = [...prev];
      const facility = { ...updated[facilityIndex] };
      
      if (field.includes('.')) {
        const parts = field.split('.');
        let current = facility;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
      } else {
        facility[field] = value;
      }
      
      updated[facilityIndex] = facility;
      return updated;
    });
    
    // Also update on the backend if we have a facility ID and the facility exists
    const facility = facilities[facilityIndex];
    if (facility && (facility.id || facility._id)) {
      const facilityId = facility.id || facility._id;
      
      // Prepare field data in proper structure
      let fieldData = {};
      if (field.includes('.')) {
        const parts = field.split('.');
        let current = fieldData;
        for (let i = 0; i < parts.length - 1; i++) {
          current[parts[i]] = {};
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
      } else {
        fieldData[field] = value;
      }
      
      // Call API to persist changes
      console.log(`Updating facility ${facilityId}, field: ${field}, value:`, value);
      
      // For specific fields that might need transformation, we handle specially
      if (field === 'facilityDetails.facilityType') {
        // When updating facility type, it needs to be at root level for backend
        callFacilityApi(facilityId, { facilityType: value });
      } else {
        callFacilityApi(facilityId, fieldData);
      }
    } else {
      console.log('Facility update is local only - no facility ID available');
    }
  }, [facilities, callFacilityApi]);
  
  const removeFacility = useCallback((facilityIndex) => {
    setFacilities(prev => prev.filter((_, index) => index !== facilityIndex));
  }, []);
  
  const addListing = useCallback((facilityIndex) => {
    setFacilities(prev => {
      const updatedFacilities = [...prev];
      const facility = { ...updatedFacilities[facilityIndex] };
      
      if (!facility.listings) {
        facility.listings = [];
      }
      
      facility.listings.push(createNewListing());
      updatedFacilities[facilityIndex] = facility;
      
      return updatedFacilities;
    });
  }, []);
  
  const updateListing = useCallback((facilityIndex, listingIndex, field, value) => {
    setFacilities(prev => {
      const updatedFacilities = [...prev];
      const facility = { ...updatedFacilities[facilityIndex] };
      
      if (!facility.listings) {
        facility.listings = [];
      }
      
      const listings = [...facility.listings];
      const listing = { ...listings[listingIndex] };
      
      if (field.includes('.')) {
        const parts = field.split('.');
        let current = listing;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) current[parts[i]] = {};
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = value;
      } else {
        listing[field] = value;
      }
      
      listings[listingIndex] = listing;
      facility.listings = listings;
      updatedFacilities[facilityIndex] = facility;
      
      return updatedFacilities;
    });
  }, []);
  
  const removeListing = useCallback((facilityIndex, listingIndex) => {
    setFacilities(prev => {
      const updatedFacilities = [...prev];
      const facility = { ...updatedFacilities[facilityIndex] };
      
      if (!facility.listings) return updatedFacilities;
      
      facility.listings = facility.listings.filter((_, index) => index !== listingIndex);
      updatedFacilities[facilityIndex] = facility;
      
      return updatedFacilities;
    });
  }, []);
  
  const handleSubmit = useCallback(() => {
    console.log('Submit triggered in legacy context');
    return { success: true };
  }, []);
  
  // Construct the legacy format context value
  const contextValue = useMemo(() => ({
      // Legacy group state and functions
      currentStep,
      setCurrentStep, // This is critical - it must be defined
      group,
      updateGroup,
      
      // Facility state and functions
      facilities,
      addFacility,
      updateFacility,
      removeFacility,
      
      // Listing functions
      addListing,
      updateListingField: updateListing, 
      updateClinicalField: updateListing,
      removeListing,
      
      // Error and validation
      errors,
      setErrors,
      validateSection: () => ({ isValid: true, errors: {} }),
      validateAll: () => ({ isValid: true, errors: {} }),
      
      // Loading and submission state
      isLoading,
      setIsLoading,
      isSubmitting,
      submitSuccess,
      existingGroupId,
      autosaveStatus,
      
      // Helper functions
      loadFromLocalStorage: () => true,
      
      // Computed values
      hasCompletedFacilities: facilities.length > 0,
      hasListings: facilities.some(f => f.listings?.length > 0),
      
      // Form submission
      handleSubmit,
      
      // Preview state
      previewFacilityIndex,
      setPreviewFacilityIndex,
      previewListingIndex,
      setPreviewListingIndex,
      
      // Modal state
      showDelFacModal,
      setShowDelFacModal,
      facToRemove, 
      setFacToRemove,
      showDelListModal,
      setShowDelListModal,
      listToRemove,
      setListToRemove,
  }), [
    // Add all dependencies used in the context value
    currentStep, setCurrentStep, group, updateGroup,
    facilities, addFacility, updateFacility, removeFacility,
    addListing, updateListing, removeListing,
    errors, setErrors, 
    isLoading, setIsLoading, isSubmitting, submitSuccess,
    existingGroupId, autosaveStatus,
    handleSubmit,
    previewFacilityIndex, setPreviewFacilityIndex,
    previewListingIndex, setPreviewListingIndex,
    showDelFacModal, setShowDelFacModal,
    facToRemove, setFacToRemove,
    showDelListModal, setShowDelListModal,
    listToRemove, setListToRemove
  ]);
  
  return (
    <GroupIntakeContext.Provider value={contextValue}>
      {children}
    </GroupIntakeContext.Provider>
  );
};

// Create a QueryClient
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false
    }
  }
});

/**
 * Bridge Provider that connects the new modular contexts to the legacy context
 * This is the component that allows both new and old components to work together
 */
export const GroupIntakeProvider = ({ children }) => {
  // Connect to auth context for checking authentication status
  const { auth } = useContext(AuthContext);
  
  // Get values from the new modular contexts
  const core = useGroupCore();
  const facility = useFacility();
  const listing = useListing();
  const error = useError();
  
  // Use the initial group model to ensure we have data even if API calls haven't completed
  // Deep clone to avoid reference issues
  const [localGroup] = useState(() => {
    console.log('Initializing with model:', initialGroup);
    return JSON.parse(JSON.stringify(initialGroup));
  });

  // For debugging - log auth status and group data
  useEffect(() => {
    console.log('GroupIntakeContext - Auth token exists:', !!auth?.token);
    console.log('GroupIntakeContext - Core group data:', core.group);
  }, [auth, core.group]);
  
  // Debug current facilities data in the facility context
  useEffect(() => {
    console.log('Bridge - Current facilities in facility context:', facility.facilities);
    // Log individual facility data to help debug
    if (Array.isArray(facility.facilities)) {
      facility.facilities.forEach((f, i) => {
        console.log(`Facility ${i}:`, f.name || f.facilityName, f);
      });
    }
  }, [facility.facilities]);

  // Construct a merged context that includes both legacy and new APIs
  const contextValue = {
    // Step navigation (critical for tab navigation)
    currentStep: core.currentStep,
    setCurrentStep: core.goToStep, // Map goToStep to setCurrentStep for legacy components
    
    // Group data - use core.group if available, otherwise use the local initial group model
    // This ensures we always have some data to show even before API calls complete
    group: Object.keys(core.group || {}).length > 0 && core.group.name ? core.group : localGroup,
    updateGroup: core.updateGroupField,
    
    // Facility data - explicitly check for array and provide fallback
    facilities: Array.isArray(facility.facilities) && facility.facilities.length > 0 ? 
      facility.facilities : 
      [createEmptyFacility()],
    addFacility: facility.addFacility,
    updateFacility: facility.updateFacilityField,
    removeFacility: facility.removeFacility,
    
    // Listing data
    addListing: listing.addListing,
    updateListingField: listing.updateListingField,
    updateClinicalField: listing.updateClinicalField,
    removeListing: listing.removeListing,
    
    // Error handling
    errors: error.errors,
    setErrors: error.setErrors,
    
    // State management
    isLoading: core.isLoading,
    isSubmitting: core.isSubmitting,
    submitSuccess: core.submitSuccess,
    
    // Preview functionality
    previewFacilityIndex: facility.previewFacilityIndex,
    previewListingIndex: listing.previewListingIndex,
    setPreviewFacilityIndex: facility.setPreviewFacilityIndex,
    setPreviewListingIndex: listing.setPreviewListingIndex,
    
    // Modal functionality
    showDelFacModal: facility.showDelFacModal,
    setShowDelFacModal: facility.setShowDelFacModal,
    facToRemove: facility.facToRemove,
    setFacToRemove: facility.setFacToRemove,
    // Add specialized facility modal functions
    openDeleteFacilityModal: facility.openDeleteFacilityModal,
    handleConfirmDeleteFacility: facility.handleConfirmDeleteFacility,
    closeDeleteFacilityModal: facility.closeDeleteFacilityModal,
    showDelListModal: listing.showDelListModal,
    setShowDelListModal: listing.setShowDelListModal,
    listToRemove: listing.listToRemove,
    setListToRemove: listing.setListToRemove,
    
    // Form submission
    handleSubmit: core.submitGroupIntake,
    
    // Autosave
    autosaveStatus: { message: 'Saved', type: 'success' },
    lastAutosave: new Date()
  };

  return (
    <GroupIntakeContext.Provider value={contextValue}>
      {children}
    </GroupIntakeContext.Provider>
  );
};

/**
 * Wrapper component that provides both the new modular contexts and the bridged legacy context
 * This is a drop-in replacement for the old GroupIntakeWithQueryProvider
 */
export const WithQueryProvider = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      <ModernGroupIntakeProvider>
        <GroupIntakeProvider>
          {children}
        </GroupIntakeProvider>
      </ModernGroupIntakeProvider>
    </QueryClientProvider>
  );
};

export { WithQueryProvider as GroupIntakeWithQueryProvider };
